// Pure settle logic for PayPal funding eligibility. The PayPal SDK can report
// `isResolved` (script onload) before `isFundingEligible` is callable; reading
// once at that instant and latching the result is the CURRENT-1850 race. This
// reducer is fed one polled read per tick and only settles on confirmation.

export type FundingRead =
  | { ready: false }
  | { ready: true; venmo: boolean; payLater: boolean };

export type SettleState = {
  settled: boolean;
  venmo: boolean;
  payLater: boolean;
  lastRead: { venmo: boolean; payLater: boolean } | null;
};

export const initialSettleState: SettleState = {
  settled: false,
  venmo: false,
  payLater: false,
  lastRead: null,
};

// Advance by one polled read. A not-ready read never settles and never latches
// a value (it preserves prior progress). Settles when two consecutive ready
// reads agree.
export function advanceSettle(
  state: SettleState,
  read: FundingRead,
): SettleState {
  if (state.settled) return state;
  if (!read.ready) return state;

  const current = { venmo: read.venmo, payLater: read.payLater };
  if (
    state.lastRead &&
    state.lastRead.venmo === current.venmo &&
    state.lastRead.payLater === current.payLater
  ) {
    return { settled: true, ...current, lastRead: current };
  }
  return { ...state, lastRead: current };
}

// Called when the poll window elapses without settling. Fail closed: hide the
// SDK-eligibility-dependent buttons rather than trust an unconfirmed read. With
// a post-isResolved cap of ~2s at 100ms cadence this only triggers when the SDK
// never became callable (a broken load), so it rarely fires in practice.
export function settleOnTimeout(state: SettleState): SettleState {
  if (state.settled) return state;
  return {
    settled: true,
    venmo: false,
    payLater: false,
    lastRead: state.lastRead,
  };
}
